import { useEffect, useState } from 'react'
import { Rating, RatingProps } from 'semantic-ui-react'
import Color from 'color'

import { FlexRow, Icon } from '@/ui'
import { useTheme } from '@/hooks'
import { RatingWrapper, SensitiveDot } from './Styles'

type HEX = `#${string}`

interface IconRatingProps {
  activeColor: HEX
  inactiveColor: HEX
  icon?: 'star' | 'heart'
  type?: 'star' | 'heart'
  maxRating: number
  onChange?: (e: any, value: OnChangeValue) => void
  defaultRating?: number
  sensitive?: boolean
  onSensitiveToggle?: (e: any, toggled: boolean) => void
  value?: string | PropValue
  rating?: string
}
interface PropValue {
  rating?: string
  activeColor?: HEX
  inactiveColor?: HEX
  icon?: string
  maxRating?: number
}
interface RateDataType extends RatingProps {
  rating: string
  activeColor: HEX
  inactiveColor: HEX
}

interface OnChangeValue {
  data?: RateDataType
  rating?: string
  activeColor?: HEX
  inactiveColor?: HEX
  value?: string | null
}

const IconRating = ({
  activeColor,
  inactiveColor,
  icon,
  maxRating,
  onChange,
  defaultRating: initialDefaultRating,
  sensitive,
  onSensitiveToggle,
  ...props
}: IconRatingProps) => {
  const { primaryColor } = useTheme()

  const [hoverColor, setHoverColor] = useState('')

  const [rating, setRating] = useState<string | number | undefined>()

  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (activeColor) {
      const color = Color(activeColor || primaryColor)
      let updatedColor
      const lightness = color.lightness()
      if (color.isLight()) {
        updatedColor = color.lightness(lightness - lightness * 0.2).hex()
      } else {
        updatedColor = color
          .lightness(lightness + (100 - lightness) * 0.2)
          .hex()
      }
      setHoverColor(updatedColor)
    }
  }, [activeColor, primaryColor])

  //Making typescript happy
  const propRating =
    typeof props.value === 'string'
      ? props.value
      : (props.value as PropValue)?.rating

  const defaultRating = initialDefaultRating || props.rating || propRating || 0

  const handleRate = (e: any, data: RatingProps) => {
    console.log(data)
    if (onChange) {
      onChange(
        e,
        !data.rating || data.rating > 0
          ? {
              ...data,
              rating: String(data.rating),
              activeColor,
              inactiveColor,
            }
          : { value: null },
      )
    }
    setRating(data.rating)
  }

  const RatingInput = (
    <Rating
      clearable
      {...props}
      onRate={handleRate}
      icon={icon || props.type}
      rating={rating ?? defaultRating}
      maxRating={maxRating}
    />
  )

  const SensitiveIcon = (
    <Icon
      button
      size="big"
      name={visible ? 'icon-sensitive-off' : 'icon-sensitive-on'}
      onClick={(e: any) => {
        onSensitiveToggle?.(e, !visible)
        setVisible(!visible)
      }}
    />
  )

  return (
    <RatingWrapper
      hoverColor={hoverColor}
      activeColor={activeColor || primaryColor}
      inactiveColor={inactiveColor}>
      {sensitive ? (
        <FlexRow noMargin alignItems="center" style={{ marginLeft: '2px' }}>
          <FlexRow.Section style={{ flexGrow: 0 }}>
            {!visible
              ? new Array(maxRating)
                  .fill(0)
                  .map((_, i) => <SensitiveDot key={i} />)
              : RatingInput}
          </FlexRow.Section>

          <FlexRow.Section>{SensitiveIcon}</FlexRow.Section>
        </FlexRow>
      ) : (
        RatingInput
      )}
    </RatingWrapper>
  )
}

export default IconRating
